import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Observable, firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';

import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';

import { FileDropZoneComponent } from '../file-drop-zone/file-drop-zone.component';
import { AttachmentsService } from '../../../service/files-and-attachments/attachments.service';
import { FileUploadService } from '../../../service/files-and-attachments/file-upload.service';
import {
  AttachmentTargetOption,
  AttachmentTargetsService,
  PartyAttachmentTargets,
  ResolvedPartyMatch,
} from '../../../service/files-and-attachments/attachment-targets.service';
import { AttachmentSheetsService } from '../../../service/google-sheets/attachment-sheets.service';
import {
  DocumentAnalysisService,
  IntakeAnalysisResponse,
  IntakeDocType,
  AI_MAX_FILE_BYTES,
  AI_FILE_TOO_LARGE_MESSAGE,
  describeAnalysisFailure,
} from '../../../service/ai/document-analysis.service';
import { BaaService } from '../../../service/entity-services/baa.service';
import { CoiService } from '../../../service/entity-services/coi.service';
import { ContractService } from '../../../service/entity-services/contract.service';
import { PartyService } from '../../../service/entity-services/party.service';
import { PeopleService } from '../../../service/entity-services/people.service';
import { UsersService } from '../../../service/entity-services/users.service';
import { AuthService } from '../../../service/auth.service';
import { Attachment } from '../../../objects/attachment';
import { EntityType } from '../../../objects/interface-library';
import { applyAttachmentTarget } from '../../utils/attachment-target';
import { settledCache } from '../../utils/cache-ready';

type View = 'idle' | 'uploading' | 'analyzing' | 'targets' | 'manual' | 'saving' | 'error';

/** The six entity types an attachment can hang off, in picker order. */
interface ManualType {
  entityType: EntityType;
  label: string;
  icon: string;
}

const MANUAL_TYPES: ManualType[] = [
  { entityType: EntityType.Contract, label: 'Contract', icon: 'description' },
  { entityType: EntityType.Party, label: 'Party / Vendor', icon: 'business' },
  { entityType: EntityType.Baa, label: 'BAA', icon: 'health_and_safety' },
  { entityType: EntityType.Coi, label: 'COI', icon: 'verified_user' },
  { entityType: EntityType.Person, label: 'Person', icon: 'person' },
  { entityType: EntityType.User, label: 'User', icon: 'account_circle' },
];

/** Keep long directories usable without virtual scrolling. */
const SEARCH_RESULT_LIMIT = 50;

/**
 * "Add an attachment" — the front door that doesn't already know where the file
 * goes.
 *
 * Every other attach path in the app starts from a record (you're looking at a
 * contract, you click Add Attachment), so the parent is implicit. This one is
 * reached from the Ask Me palette with no context at all, which is exactly the
 * case NewAttachmentComponent can't serve: it hard-requires `entityObject` and
 * refuses to save without one.
 *
 * So the job here is to *find* the parent. The user drops a file; we upload it
 * once, ask the intake AI what it is and who it's between, then offer the
 * matched party's contracts, BAAs and COIs as destinations with a best guess
 * pre-selected. AI is a shortcut, never a requirement — declining it, a
 * non-PDF, or any failure lands in the same manual picker, and nothing is ever
 * saved without an explicit click.
 */
@Component({
  selector: 'app-attach-document-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    MatSelectModule,
    FileDropZoneComponent,
  ],
  templateUrl: './attach-document-dialog.component.html',
  styleUrls: ['./attach-document-dialog.component.css'],
})
export class AttachDocumentDialogComponent implements OnInit {
  private dialogRef = inject(MatDialogRef<AttachDocumentDialogComponent>);
  private fileUploadService = inject(FileUploadService);
  private attachmentsService = inject(AttachmentsService);
  private attachmentSheetsService = inject(AttachmentSheetsService);
  private documentAnalysisService = inject(DocumentAnalysisService);
  private targetsService = inject(AttachmentTargetsService);
  private partyService = inject(PartyService);
  private contractService = inject(ContractService);
  private baaService = inject(BaaService);
  private coiService = inject(CoiService);
  private peopleService = inject(PeopleService);
  private usersService = inject(UsersService);
  private authService = inject(AuthService);

  /** The signed-in user's {org}-Users record, used as the attachment's owner. */
  private currentUser: any = undefined;

  readonly view = signal<View>('idle');
  readonly statusMessage = signal('');
  readonly errorMessage = signal('');

  readonly selectedFile = signal<File | null>(null);
  /** Set once the file is in S3 — reused by every downstream path, so we upload exactly once. */
  private s3Url = '';
  private s3FileName = '';

  // ── AI results ──────────────────────────────────────────────
  readonly analysis = signal<IntakeAnalysisResponse | null>(null);
  readonly classification = signal<IntakeDocType | undefined>(undefined);
  readonly partyMatches = signal<ResolvedPartyMatch[]>([]);
  readonly activePartyIndex = signal(0);
  readonly targets = signal<PartyAttachmentTargets | null>(null);
  /** True while the user is swapping the AI's party for one they searched. */
  readonly swappingParty = signal(false);

  readonly activeMatch = computed<ResolvedPartyMatch | null>(
    () => this.partyMatches()[this.activePartyIndex()] ?? null
  );

  /** Groups with nothing in them are hidden rather than shown empty. */
  readonly hasAnyTarget = computed(() => {
    const t = this.targets();
    return !!t && (t.contracts.length > 0 || t.baas.length > 0 || t.cois.length > 0);
  });

  // ── Manual picker ───────────────────────────────────────────
  readonly manualTypes = MANUAL_TYPES;
  readonly manualType = signal<EntityType>(EntityType.Contract);
  readonly manualQuery = signal('');
  readonly manualResults = signal<AttachmentTargetOption[]>([]);
  readonly manualLoading = signal(false);
  /**
   * How many records matched before the display cap. Surfaced in the UI when it
   * exceeds what's shown — a truncated list that looks complete would let a user
   * conclude their record doesn't exist when it's simply on the cut side of 50.
   */
  readonly manualTotalMatches = signal(0);
  /** Monotonic id so an out-of-order directory load can't repaint a newer list. */
  private searchToken = 0;
  /** Why we ended up in the manual picker, shown inline. Empty when the user chose it. */
  readonly manualReason = signal('');

  // ── The chosen destination + metadata ───────────────────────
  readonly selectedTarget = signal<AttachmentTargetOption | null>(null);
  readonly documentTitle = signal('');
  readonly attachmentType = signal('');
  readonly description = signal('');
  readonly attachmentTypes = signal<string[]>([]);

  readonly canSave = computed(
    () => !!this.selectedTarget() && this.documentTitle().trim().length > 0 && this.view() !== 'saving'
  );

  /** True when the result list is showing fewer rows than actually matched. */
  readonly manualTruncated = computed(() => this.manualTotalMatches() > this.manualResults().length);
  readonly manualHiddenCount = computed(() =>
    Math.max(0, this.manualTotalMatches() - this.manualResults().length)
  );

  ngOnInit(): void {
    // Warm every directory this dialog can pick from. Ask Me opens from anywhere,
    // so we can't assume the user has visited the list that populates a cache.
    // Each service dedupes/no-ops if it's already loaded.
    // Warm only what's actually cold. These refreshes are NOT idempotent: each
    // one re-streams the full paginated load (~45 pages / 4,500 vendors on
    // cbhi) into the app-wide subject, so re-running them would make every grid
    // and autocomplete behind this dialog rebuild from 100 → 4,500 rows — and
    // PartyService pushes `[]` on failure, which would wipe the shared cache for
    // the rest of the session. PeopleService, BaaService and CoiService refresh
    // in their own constructors, so injecting them above already started those.
    this.refreshIfEmpty(this.partyService.allParties$, () =>
      this.partyService.refreshAllPartiesRecords().subscribe({ error: () => {} })
    );
    this.refreshIfEmpty(this.contractService.allContracts$, () =>
      this.contractService.refreshAllContractsRecords()
    );
    this.refreshIfEmpty(this.usersService.allUsers$, () => this.usersService.refreshAllUsersRecords());

    this.attachmentSheetsService.getAttachmentTypes().subscribe({
      next: (types) => this.attachmentTypes.set(Array.isArray(types) ? types : []),
      error: () => this.attachmentTypes.set([]),
    });

    void this.resolveCurrentUser();
  }

  /**
   * Look up the signed-in user's {org}-Users record so saved attachments carry
   * an owner. Uses AuthService.getUserEmail(), which works for both the SSO and
   * username/password paths. Best-effort — a miss just leaves owner unset.
   */
  private async resolveCurrentUser(): Promise<void> {
    try {
      const email = (await this.authService.getUserEmail())?.toLowerCase().trim();
      if (!email) return;
      const users = await settledCache(this.usersService.allUsers$ as any);
      this.currentUser = (users ?? []).find(
        (u: any) => (u?.email ?? '').toLowerCase().trim() === email
      );
    } catch {
      // Owner is a nicety, never a reason to block the attach flow.
    }
  }

  /**
   * Kick a cache refresh only when it holds nothing. Reads the current value
   * synchronously — every cache here is BehaviorSubject-backed, so the
   * subscription fires immediately and unsubscribes before returning.
   */
  private refreshIfEmpty(source: Observable<any[]>, refresh: () => void): void {
    let isEmpty = false;
    source.pipe(take(1)).subscribe((items) => (isEmpty = !items || items.length === 0));
    if (isEmpty) refresh();
  }

  // ── Step 1: file ────────────────────────────────────────────

  onFileSelected(file: File): void {
    this.selectedFile.set(file);
    this.errorMessage.set('');
    // Filename minus extension is nearly always the title the user wants; they
    // can overwrite it before saving.
    this.documentTitle.set(file.name.replace(/\.[^.]+$/, ''));
  }

  get isPdf(): boolean {
    const file = this.selectedFile();
    return !!file && file.name.toLowerCase().endsWith('.pdf');
  }

  /** Over the cap the AI step is skipped; the file still attaches by hand. */
  get isTooLargeForAi(): boolean {
    return (this.selectedFile()?.size ?? 0) > AI_MAX_FILE_BYTES;
  }

  readonly tooLargeForAiMessage = AI_FILE_TOO_LARGE_MESSAGE;

  /** "Find where this goes" — upload, then ask the AI. */
  async analyze(): Promise<void> {
    const file = this.selectedFile();
    if (!file) return;

    try {
      await this.ensureUploaded(file);
    } catch {
      return; // ensureUploaded already surfaced the error view
    }

    this.view.set('analyzing');
    this.statusMessage.set('Reading the document…');

    let response: IntakeAnalysisResponse | null = null;
    try {
      response = await firstValueFrom(
        this.documentAnalysisService.triggerIntakeAnalysis(this.s3Url, file.name, (progress) =>
          this.statusMessage.set(progress.message)
        )
      );
    } catch (err) {
      console.error('[AttachDocument] intake analysis failed:', err);
    }

    if (!response || response.status === 'failed' || !response.extraction) {
      // The file is already safely in S3, so a failed analysis costs the user
      // nothing but the suggestion — drop them into manual selection.
      const failure = describeAnalysisFailure(response?.errorCode);
      const reference = response?.analysisId ? ` (Reference: ${response.analysisId})` : '';
      this.toManual(`${failure.message} Choose where it should go.${reference}`);
      return;
    }

    // Everything past here is local cache work, but it still has to be guarded:
    // an unhandled throw would strand the user on the 'analyzing' spinner, and
    // this dialog opens with disableClose so there'd be no way out but a reload.
    try {
      this.analysis.set(response);
      this.classification.set(response.classification ?? response.extraction?.classification?.docType);

      const matches = await this.targetsService.resolvePartyMatches(response.partyMatches);
      this.partyMatches.set(matches);

      if (matches.length === 0) {
        this.toManual("I couldn't match a party in that document. Choose where it should go.");
        return;
      }

      this.activePartyIndex.set(0);
      await this.loadTargetsForActiveParty();
      this.view.set('targets');
    } catch (err) {
      console.error('[AttachDocument] building suggestions failed:', err);
      this.toManual("I couldn't work out where this belongs. Choose where it should go.");
    }
  }

  /** "I'll choose myself" — upload, then straight to the picker. */
  async chooseManually(): Promise<void> {
    const file = this.selectedFile();
    if (!file) return;
    try {
      await this.ensureUploaded(file);
    } catch {
      return;
    }
    this.toManual('');
  }

  /**
   * Upload once, on the first path that needs it. Both entry points call this,
   * and switching between them afterwards must not re-upload — the S3 object is
   * what the attachment's directLink points at.
   */
  private async ensureUploaded(file: File): Promise<void> {
    if (this.s3Url) return;

    this.view.set('uploading');
    this.statusMessage.set('Uploading…');
    try {
      const extension = file.name.split('.').pop();
      // Same `intake/` prefix Drop-and-Reveal uses. The analysis Lambda already
      // reads that prefix, so staying on it avoids depending on a broader S3
      // grant that may not exist for every org's bucket.
      const uniqueFileName = `intake/${Date.now()}_${Math.random().toString(36).substring(2, 15)}.${extension}`;
      const presigned = await firstValueFrom(this.fileUploadService.getPresignedUrl(uniqueFileName));
      await firstValueFrom(this.fileUploadService.uploadFileToS3(presigned.presignedUrl, file));
      this.s3Url = presigned.presignedUrl.split('?')[0];
      this.s3FileName = uniqueFileName;
    } catch (err) {
      console.error('[AttachDocument] upload failed:', err);
      this.errorMessage.set('Failed to upload the file. Please try again.');
      this.view.set('error');
      throw err;
    }
  }

  // ── Step 2a: AI suggestions ─────────────────────────────────

  private async loadTargetsForActiveParty(): Promise<void> {
    const match = this.activeMatch();
    if (!match) return;

    this.statusMessage.set('Looking up what belongs to this party…');
    const targets = await this.targetsService.targetsForParty(match.party);
    this.targets.set(targets);
    this.selectedTarget.set(
      this.targetsService.preselect(this.classification(), this.analysis(), targets)
    );
  }

  /** Cycle to the next candidate party the AI returned. */
  async useNextPartyMatch(): Promise<void> {
    const matches = this.partyMatches();
    if (matches.length < 2) return;
    this.activePartyIndex.set((this.activePartyIndex() + 1) % matches.length);
    await this.loadTargetsForActiveParty();
  }

  /** Open the party search so the user can override the AI's match entirely. */
  startPartySwap(): void {
    this.swappingParty.set(true);
    this.manualType.set(EntityType.Party);
    this.manualQuery.set('');
    this.manualResults.set([]);
  }

  cancelPartySwap(): void {
    this.swappingParty.set(false);
    this.manualQuery.set('');
    this.manualResults.set([]);
  }

  /**
   * A party picked from the swap search replaces the AI's match entirely —
   * including the other candidates, since the user has told us none of them
   * were right. Flagged `userChosen` so the header shows no confidence score.
   */
  async usePartyFromSearch(option: AttachmentTargetOption): Promise<void> {
    this.swappingParty.set(false);
    this.manualQuery.set('');
    this.manualResults.set([]);
    this.partyMatches.set([
      {
        party: option.entity,
        similarity: 0,
        matchConfidence: 'none',
        extractedName: '',
        userChosen: true,
      },
    ]);
    this.activePartyIndex.set(0);
    await this.loadTargetsForActiveParty();
  }

  selectTarget(option: AttachmentTargetOption): void {
    this.selectedTarget.set(option);
  }

  isSelected(option: AttachmentTargetOption): boolean {
    return this.selectedTarget()?.key === option.key;
  }

  /** Drop out of the suggestions into the full manual picker. */
  toManualFromTargets(): void {
    this.toManual('');
  }

  // ── Step 2b: manual picker ──────────────────────────────────

  private toManual(reason: string): void {
    this.manualReason.set(reason);
    this.manualType.set(EntityType.Contract);
    this.manualQuery.set('');
    this.manualResults.set([]);
    this.manualTotalMatches.set(0);
    this.selectedTarget.set(null);
    this.swappingParty.set(false);
    this.view.set('manual');
    // Populate straight away rather than waiting for a keystroke — the Contract
    // chip renders as selected, so an empty list beneath it reads as "there are
    // no contracts" instead of "start typing".
    void this.runManualSearch();
  }

  async setManualType(entityType: EntityType): Promise<void> {
    this.manualType.set(entityType);
    this.manualResults.set([]);
    // Drop the selection with the list it came from. Without this the Attach
    // button stays live pointing at a row from the PREVIOUS type — the user
    // sees a vendor list with nothing highlighted, clicks Attach, and the file
    // silently lands on the contract they picked a moment ago.
    this.selectedTarget.set(null);
    await this.runManualSearch();
  }

  async onManualQueryChange(value: string): Promise<void> {
    this.manualQuery.set(value);
    await this.runManualSearch();
  }

  /**
   * Search one directory at a time. Typeahead-only for the big directories —
   * an empty box shows nothing rather than dumping thousands of vendors — but
   * the small ones list up front so the user isn't guessing at what exists.
   */
  private async runManualSearch(): Promise<void> {
    const query = this.manualQuery().trim().toLowerCase();
    const entityType = this.manualType();

    // Directories settle at very different speeds (a warm contracts cache in
    // ~400ms, 4,500 streaming vendors in seconds). Without a token, a slow
    // earlier request resolves last and repaints its rows under whichever chip
    // is now selected — showing vendors while "Contract" is highlighted.
    const token = ++this.searchToken;
    this.manualLoading.set(true);
    try {
      const records = (await this.recordsFor(entityType)).filter((option) =>
        // Never offer a row Attach would reject — a record missing the id field
        // its type links against is a dead end, not a choice.
        this.targetsService.isAttachable(option)
      );
      if (token !== this.searchToken) return; // superseded; discard
      const matched = query
        ? records.filter((option) => this.optionMatches(option, query))
        : records;
      this.manualTotalMatches.set(matched.length);
      this.manualResults.set(matched.slice(0, SEARCH_RESULT_LIMIT));
    } finally {
      // Only the newest request owns the spinner, or an early finisher would
      // clear it while the current one is still in flight.
      if (token === this.searchToken) this.manualLoading.set(false);
    }
  }

  private optionMatches(option: AttachmentTargetOption, query: string): boolean {
    return (
      option.label.toLowerCase().includes(query) || option.sublabel.toLowerCase().includes(query)
    );
  }

  /** One directory, mapped into the same option shape the suggestion lists use. */
  private async recordsFor(entityType: EntityType): Promise<AttachmentTargetOption[]> {
    switch (entityType) {
      case EntityType.Contract: {
        // visibleContracts, not allContracts — archived contracts are excluded
        // from the AI suggestions too (contractsForParty reads the same subject),
        // so the two lists can't disagree about what's attachable.
        const contracts = await settledCache(this.contractService.visibleContracts$);
        return (contracts ?? []).map((c) => this.targetsService.contractOption(c));
      }
      case EntityType.Party: {
        const parties = await settledCache(this.partyService.allParties$);
        return (parties ?? []).map((p) => this.targetsService.partyOption(p));
      }
      case EntityType.Baa: {
        const baas = await settledCache(this.baaService.allBaas$);
        return (baas ?? []).map((b) => this.targetsService.baaOption(b));
      }
      case EntityType.Coi: {
        const cois = await settledCache(this.coiService.allCois$);
        return (cois ?? []).map((c) => this.targetsService.coiOption(c));
      }
      case EntityType.Person: {
        const people = await settledCache(this.peopleService.allPeople$ as any);
        return (people ?? []).map((p: any) => this.personOption(p));
      }
      case EntityType.User: {
        const users = await settledCache(this.usersService.allUsers$ as any);
        return (users ?? []).map((u: any) => this.userOption(u));
      }
      default:
        return [];
    }
  }

  private personOption(person: any): AttachmentTargetOption {
    const num = person?.contactNum ? String(person.contactNum) : '';
    return {
      entity: { ...person, entityType: EntityType.Person },
      entityType: EntityType.Person,
      label: person?.friendlyName || person?.fullName || num || 'Unnamed person',
      sublabel: person?.email || '',
      key: `person:${num}`,
    };
  }

  private userOption(user: any): AttachmentTargetOption {
    const num = user?.userNum ? String(user.userNum) : '';
    return {
      entity: { ...user, entityType: EntityType.User },
      entityType: EntityType.User,
      label: user?.friendlyName || user?.email || num || 'Unnamed user',
      sublabel: user?.email || '',
      key: `user:${num}`,
    };
  }

  // ── Step 3: save ────────────────────────────────────────────

  async save(): Promise<void> {
    const target = this.selectedTarget();
    const file = this.selectedFile();
    if (!target || !file || !this.s3Url) return;

    const attachment: Attachment = {
      attachmentNum: Date.now().toString(),
      name: this.documentTitle().trim(),
      description: this.description().trim(),
      attachmentType: this.attachmentType() || undefined,
      s3filename: this.s3FileName,
      directLink: this.s3Url,
      fileSize: file.size,
      originalFileName: file.name,
      createdDate: new Date().toISOString(),
      // NewAttachmentComponent asks the user to pick an owner; here there's no
      // sensible person to nominate other than whoever is uploading, so default
      // to them rather than leaving the attachments grid's Owner column blank.
      ...(this.currentUser ? { owner: this.currentUser } : {}),
    } as Attachment;

    // Same guard NewAttachmentComponent applies — an attachment whose parent id
    // can't be resolved is orphaned and unreachable from every list in the app.
    if (!applyAttachmentTarget(attachment, target.entity)) {
      this.errorMessage.set(
        `"${target.label}" is missing the id needed to link an attachment. Pick a different destination.`
      );
      return;
    }

    // Remember which picker they came from: a user who started with AI
    // suggestions and then switched to manual selection still has targets()
    // populated, so inferring the origin from that would send them back to the
    // wrong screen after a failed save.
    const origin = this.view();
    this.view.set('saving');
    this.errorMessage.set('');
    try {
      await firstValueFrom(this.attachmentsService.saveAttachment(attachment));
      this.attachmentsService.refreshAllAttachmentsRecords();
      this.dialogRef.close(attachment);
    } catch (err) {
      console.error('[AttachDocument] save failed:', err);
      this.errorMessage.set('Failed to save the attachment. Please try again.');
      // Back where they were, selection intact and the file still in S3.
      this.view.set(origin);
    }
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  /**
   * From the error view: back to the drop zone for a clean retry. Clears the S3
   * pointer too, so the retry re-uploads rather than reusing an object from a
   * run that failed partway (the previous object is left orphaned in the bucket,
   * same as any abandoned Drop-and-Reveal upload).
   */
  startOver(): void {
    this.s3Url = '';
    this.s3FileName = '';
    this.errorMessage.set('');
    this.statusMessage.set('');
    this.analysis.set(null);
    this.classification.set(undefined);
    this.targets.set(null);
    this.partyMatches.set([]);
    this.activePartyIndex.set(0);
    this.selectedTarget.set(null);
    this.swappingParty.set(false);
    this.manualReason.set('');
    this.manualQuery.set('');
    this.manualResults.set([]);
    this.manualTotalMatches.set(0);
    this.view.set('idle');
  }

  // ── Display helpers ─────────────────────────────────────────

  readonly docTypeLabels: Record<string, string> = {
    contract: 'a contract',
    baa: 'a BAA',
    coi: 'a certificate of insurance',
    addendum: 'an addendum',
    unknown: 'a document',
  };

  classificationLabel(): string {
    return this.docTypeLabels[this.classification() ?? 'unknown'] ?? 'a document';
  }

  matchPercent(match: ResolvedPartyMatch | null): number {
    return Math.round((match?.similarity ?? 0) * 100);
  }
}
